'use client'

import { useEffect, useState } from 'react'
import { Spacer } from '@vinted/web-ui'
import classNames from 'classnames'
import { QuickReplies24, FastShipper24, FrequentUploader24 } from '@vinted/monochrome-icons'

import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  clickEvent,
  getSellerBadges,
  SellerBadgeType,
  transformSellerBadgesResponse,
  SellerBadgeDto,
} from '@marketplace-web/seller-engagement-tools/seller-engagement-tools-data'

import {
  MAP_BADGE_TYPE_TO_BADGE_TARGET,
  ReplyTimeThreshold,
  SpeedyShippingProgress,
} from '../../constants/badges'
import { getQuickRepliesProgress } from '../../utils/badges'
import BadgeBanner from './BadgeBanner'
import BadgeInfoModal from './BadgeInfoModal'

const ProfileBadges = () => {
  const translate = useTranslate('profile.badges')
  const translateA11y = useTranslate('user.seller_badges.a11y')
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const { user } = useSession()

  const showProfileBadges = user && !user.business

  const profileReworkAbTest = useAbTest('profile_wardrobe_web_rework')
  const isProfileReworkTestOn = profileReworkAbTest?.variant === 'on' && !breakpoints.portables

  const [selectedBadge, setSelectedBadge] = useState<SellerBadgeDto | null>(null)

  const { transformedData: badges, fetch: fetchBadges } = useFetch(
    getSellerBadges,
    transformSellerBadgesResponse,
  )

  const classes = classNames({
    'u-flexbox': breakpoints.tabletsUp,
    'u-flex-direction-column': isProfileReworkTestOn,
  })

  useEffect(() => {
    if (!showProfileBadges) return

    fetchBadges()
  }, [fetchBadges, showProfileBadges])

  const handleOnClick = (badge: SellerBadgeDto) => () => {
    track(
      clickEvent({
        screen: 'current_user_profile',
        target: MAP_BADGE_TYPE_TO_BADGE_TARGET[badge.type],
      }),
    )
    setSelectedBadge(badge)
  }

  const getTitleTranslation = (badge: SellerBadgeDto) => {
    const { progress } = badge

    switch (badge.type) {
      case SellerBadgeType.ActiveLister: {
        if (progress.current === 0) return translate('frequent_uploads.title')
        if (progress.current < progress.goal)
          return translate(
            'frequent_uploads.title_progress',
            { items_count: progress.current },
            { count: progress.current },
          )

        return translate('frequent_uploads.title_success')
      }

      case SellerBadgeType.FastReplier: {
        if (progress.current === ReplyTimeThreshold.VerySlow)
          return translate('quick_replier.title')
        if (progress.current <= progress.goal) return translate('quick_replier.title_success')
        if (progress.current > progress.goal && progress.current <= ReplyTimeThreshold.Slow)
          return translate('quick_replier.title_progress', { number_hours: progress.current })

        return translate('quick_replier.title')
      }

      case SellerBadgeType.SpeedyShipper: {
        if (progress.current === SpeedyShippingProgress.None)
          return translate('speedy_shipping.title')
        if (progress.current === SpeedyShippingProgress.Quarter)
          return translate('speedy_shipping.title_progress.progress_25')
        if (progress.current === SpeedyShippingProgress.Half)
          return translate('speedy_shipping.title_progress.progress_50')
        if (progress.current === SpeedyShippingProgress.ThreeQuarters)
          return translate('speedy_shipping.title_progress.progress_75')

        return translate('speedy_shipping.title_success')
      }
      default:
        return ''
    }
  }

  const getBodyTranslation = (badge: SellerBadgeDto) => {
    const { progress } = badge

    switch (badge.type) {
      case SellerBadgeType.ActiveLister: {
        if (progress.current === 0)
          return translate('frequent_uploads.body', { items_count: progress.goal })
        if (progress.current < progress.goal) return translate('frequent_uploads.body_progress')

        return translate('frequent_uploads.body_success')
      }

      case SellerBadgeType.FastReplier: {
        if (progress.current === ReplyTimeThreshold.VerySlow) return translate('quick_replier.body')
        if (progress.current <= progress.goal) return translate('quick_replier.body_success')
        if (progress.current > progress.goal && progress.current <= ReplyTimeThreshold.Slow)
          return translate('quick_replier.body_progress')

        return translate('quick_replier.body')
      }

      case SellerBadgeType.SpeedyShipper: {
        if (progress.current === 0) return translate('speedy_shipping.body')
        if (progress.current < progress.goal) return translate('speedy_shipping.body_progress')

        return translate('speedy_shipping.body_success')
      }
      default:
        return ''
    }
  }

  const getIconProps = (badge: SellerBadgeDto) => {
    const { progress } = badge

    switch (badge.type) {
      case SellerBadgeType.ActiveLister: {
        return {
          icon: FrequentUploader24,
          progress: progress.current / progress.goal,
        }
      }
      case SellerBadgeType.FastReplier: {
        return {
          icon: QuickReplies24,
          progress: getQuickRepliesProgress(progress.current),
        }
      }
      case SellerBadgeType.SpeedyShipper: {
        return {
          icon: FastShipper24,
          progress: progress.current / progress.goal,
        }
      }
      default:
        return { icon: null, progress: 0 }
    }
  }

  function renderSeparator() {
    return breakpoints.phones || isProfileReworkTestOn ? (
      <Spacer orientation={Spacer.Orientation.Horizontal} size={Spacer.Size.Medium} />
    ) : (
      <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Large} />
    )
  }

  const renderBadge = (badge: SellerBadgeDto | undefined) => {
    if (!badge) return null

    return (
      <BadgeBanner
        key={badge.type}
        titleText={getTitleTranslation(badge)}
        bodyText={getBodyTranslation(badge)}
        iconProps={getIconProps(badge)}
        onClick={handleOnClick(badge)}
      />
    )
  }

  const renderProfileBadges = () => {
    if (!badges?.length) return null

    return (
      <div className={classes} aria-label={translateA11y('group_title')}>
        <SeparatedList separator={renderSeparator()}>
          {badges.map(badge => renderBadge(badge))}
        </SeparatedList>
      </div>
    )
  }

  if (!badges?.length) return null

  return (
    <>
      <Spacer size={Spacer.Size.Medium} />
      {renderProfileBadges()}
      <BadgeInfoModal selectedBadge={selectedBadge} onClose={() => setSelectedBadge(null)} />
    </>
  )
}

export default ProfileBadges
